/**
 * Optimizer Control Center — generic confirm dialog for row actions
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, ReactNode } from 'react';

import { Button, LoadingSpinner } from '@/components/ui';
import { backdropClick } from '@/components/ui';

export interface ConfirmActionDialogProps {
  title: string;
  description: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  isBusy?: boolean;
  /** Disables ONLY the confirm button (e.g. an invalid in-dialog choice). */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmActionDialog: FC<ConfirmActionDialogProps> = ({
  title,
  description,
  confirmLabel,
  destructive = false,
  isBusy = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}) => (
  <div
    className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95 p-4 backdrop-blur-sm"
    onClick={backdropClick(!isBusy ? onCancel : undefined)}
    role="dialog"
    aria-modal="true"
    aria-label={title}
  >
    <div
      className="relative w-full max-w-md rounded-xl border border-border bg-card shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="border-b border-border bg-muted/20 px-4 py-3">
        <h3 className="text-base font-semibold">{title}</h3>
      </div>
      <div className="p-4 text-sm text-muted-foreground">{description}</div>
      <div className="flex justify-end gap-2 border-t border-border bg-muted/10 px-4 py-3">
        <Button variant="outline" onClick={onCancel} disabled={isBusy}>
          Cancel
        </Button>
        <Button
          variant={destructive ? 'destructive' : 'default'}
          onClick={onConfirm}
          disabled={isBusy || confirmDisabled}
          aria-busy={isBusy}
          className="gap-2"
        >
          {isBusy && <LoadingSpinner size="sm" />}
          {confirmLabel}
        </Button>
      </div>
    </div>
  </div>
);
